import React from 'react';
import StickyHeader from '../components/StickyHeader';
import Footer from '../components/Footer';

const PrivacyPage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Sticky Navigation */}
      <StickyHeader />

      {/* Hero */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-8 max-w-3xl">
          <h1 className="text-4xl md:text-5xl font-semibold tracking-tight mb-6">
            Privacy Policy
          </h1>
          <p className="text-slate-500 text-sm">
            Last updated: January 2025
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="pb-24">
        <div className="container mx-auto px-4 md:px-8 max-w-3xl">
          <div className="prose prose-slate max-w-none">
            
            {/* Intro */}
            <p className="text-lg text-slate-600 leading-relaxed mb-8">
              At ComplyKit, we take your privacy seriously. This policy explains what information 
              we collect, why we collect it, and how we protect it. We've written this in plain 
              language so you can actually understand it.
            </p>

            {/* What we collect */}
            <section className="mb-10">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">What we collect</h2>
              <p className="text-slate-600 mb-4">
                We collect information necessary to provide our compliance services:
              </p>
              <ul className="space-y-2 text-slate-600">
                <li className="flex items-start gap-3">
                  <span className="h-1.5 w-1.5 bg-slate-400 rounded-full mt-2.5 flex-shrink-0" />
                  <span><strong className="text-slate-900">Personal information:</strong> Name, email address, phone number, and address</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="h-1.5 w-1.5 bg-slate-400 rounded-full mt-2.5 flex-shrink-0" />
                  <span><strong className="text-slate-900">Identity documents:</strong> PAN, Aadhaar, passport, or other IDs required for filings</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="h-1.5 w-1.5 bg-slate-400 rounded-full mt-2.5 flex-shrink-0" />
                  <span><strong className="text-slate-900">Business information:</strong> Company details, GST numbers, financial documents as needed for compliance services</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="h-1.5 w-1.5 bg-slate-400 rounded-full mt-2.5 flex-shrink-0" />
                  <span><strong className="text-slate-900">Usage data:</strong> How you interact with our platform to improve our services</span>
                </li>
              </ul>
            </section>

            {/* Why we collect */}
            <section className="mb-10">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">Why we collect it</h2>
              <p className="text-slate-600 mb-4">
                We use your information for:
              </p>
              <ul className="space-y-2 text-slate-600">
                <li className="flex items-start gap-3">
                  <span className="h-1.5 w-1.5 bg-slate-400 rounded-full mt-2.5 flex-shrink-0" />
                  <span><strong className="text-slate-900">Service delivery:</strong> Processing your compliance filings, registrations, and related tasks</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="h-1.5 w-1.5 bg-slate-400 rounded-full mt-2.5 flex-shrink-0" />
                  <span><strong className="text-slate-900">Communication:</strong> Sending updates about your filings, deadlines, and important notifications</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="h-1.5 w-1.5 bg-slate-400 rounded-full mt-2.5 flex-shrink-0" />
                  <span><strong className="text-slate-900">Legal compliance:</strong> Meeting regulatory requirements and maintaining records as required by law</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="h-1.5 w-1.5 bg-slate-400 rounded-full mt-2.5 flex-shrink-0" />
                  <span><strong className="text-slate-900">Service improvement:</strong> Understanding how our platform is used to make it better</span>
                </li>
              </ul>
            </section>

            {/* How we protect */}
            <section className="mb-10">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">How we protect your data</h2>
              <p className="text-slate-600 mb-4">
                Your data security is a priority:
              </p>
              <ul className="space-y-2 text-slate-600">
                <li className="flex items-start gap-3">
                  <span className="h-1.5 w-1.5 bg-slate-400 rounded-full mt-2.5 flex-shrink-0" />
                  <span>All data is encrypted in transit and at rest</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="h-1.5 w-1.5 bg-slate-400 rounded-full mt-2.5 flex-shrink-0" />
                  <span>Access to sensitive information is restricted to authorized personnel only</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="h-1.5 w-1.5 bg-slate-400 rounded-full mt-2.5 flex-shrink-0" />
                  <span>We use secure, industry-standard cloud infrastructure</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="h-1.5 w-1.5 bg-slate-400 rounded-full mt-2.5 flex-shrink-0" />
                  <span>Regular security audits and vulnerability assessments</span>
                </li>
              </ul>
            </section>

            {/* Sharing */}
            <section className="mb-10">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">When we share information</h2>
              <p className="text-slate-600 mb-4">
                We share your information only when necessary:
              </p>
              <ul className="space-y-2 text-slate-600">
                <li className="flex items-start gap-3">
                  <span className="h-1.5 w-1.5 bg-slate-400 rounded-full mt-2.5 flex-shrink-0" />
                  <span><strong className="text-slate-900">With compliance professionals:</strong> CAs, Company Secretaries, and lawyers who work on your filings</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="h-1.5 w-1.5 bg-slate-400 rounded-full mt-2.5 flex-shrink-0" />
                  <span><strong className="text-slate-900">With government portals:</strong> When submitting filings to MCA, GST, Income Tax, or other regulatory bodies</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="h-1.5 w-1.5 bg-slate-400 rounded-full mt-2.5 flex-shrink-0" />
                  <span><strong className="text-slate-900">When required by law:</strong> In response to valid legal requests or court orders</span>
                </li>
              </ul>
              <div className="mt-6 p-4 bg-slate-50 rounded-lg">
                <p className="text-slate-900 font-medium">We never sell your personal data.</p>
                <p className="text-sm text-slate-600 mt-1">Your information is not shared with advertisers or sold to third parties for marketing purposes.</p>
              </div>
            </section>

            {/* Your rights */}
            <section className="mb-10">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">Your rights</h2>
              <p className="text-slate-600 mb-4">
                You have control over your data:
              </p>
              <ul className="space-y-2 text-slate-600">
                <li className="flex items-start gap-3">
                  <span className="h-1.5 w-1.5 bg-slate-400 rounded-full mt-2.5 flex-shrink-0" />
                  <span><strong className="text-slate-900">Access:</strong> Request a copy of the personal data we hold about you</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="h-1.5 w-1.5 bg-slate-400 rounded-full mt-2.5 flex-shrink-0" />
                  <span><strong className="text-slate-900">Correction:</strong> Update or correct inaccurate information</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="h-1.5 w-1.5 bg-slate-400 rounded-full mt-2.5 flex-shrink-0" />
                  <span><strong className="text-slate-900">Deletion:</strong> Request deletion of your data (subject to legal retention requirements)</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="h-1.5 w-1.5 bg-slate-400 rounded-full mt-2.5 flex-shrink-0" />
                  <span><strong className="text-slate-900">Portability:</strong> Receive your data in a structured, machine-readable format</span>
                </li>
              </ul>
            </section>

            {/* Contact */}
            <section className="p-6 bg-slate-900 rounded-xl text-white">
              <h2 className="text-xl font-semibold mb-3">Questions about privacy?</h2>
              <p className="text-slate-300 mb-4">
                If you have questions about this policy or want to exercise your data rights, 
                contact our privacy team:
              </p>
              <a 
                href="mailto:privacy@complykit.in" 
                className="text-white font-medium hover:underline"
              >
                privacy@complykit.in
              </a>
            </section>

          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PrivacyPage;
